import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  SwitchProps
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { getAddon, validateCoupon } from 'src/common/Scraper';
import CouponSection from 'src/content/onBoarding/CouponSection';
import { useBuyOrbitAIAddon } from '../hooks/useBuyOrbitAIAddon';
import CommonModal from 'src/common/config/CommonModal';
import CommonAlert from 'src/common/CommonAlert';
import { GenerateIdeasLoader } from 'src/content/keywordResearch/blogPostIdeas/GenerateIdeasLoader';
import { updateSearchesDetailsAndLimits } from 'src/common/config/FunctionHelperPartTwo';
import { setSelectedSearches } from 'src/redux/keywordResearch';

const PricingSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 44,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#139686',
        opacity: 1,
        border: 0
      }
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    backgroundColor: '#D0D5DD',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300
    })
  }
}));

interface OrbitAIAddonModalProps {
  open: boolean;
  onClose: () => void;
}

export const OrbitAIAddonContent = ({
  handleCloseModal,
  handleCloseProcessModal,
  setOpenProcessModal,
  setSnackBarData = () => {}
}: {
  handleCloseModal: () => void;
  handleCloseProcessModal: () => void;
  setOpenProcessModal: (open: boolean) => void;
  setSnackBarData?: (value: any) => void;
}) => {
  const [creditData, setCreditData] = useState<any[]>([]);
  const [planId, setPlanId] = useState<string>('');
  const [alertData, setAlertData] = useState<any>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [discountedPrice, setDiscountedPrice] = useState<any>(null);

  const userEmail = useSelector((state: RootState) => state.counter.userEmail);
  const dispatch = useDispatch();
  const { mutateAsync: buyOrbitAIAddon, isPending } = useBuyOrbitAIAddon();
  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const isMember = userDetails?.role_type === 'member';

  useEffect(() => {
    getAddonPlans();
  }, []);

  const getAddonPlans = async () => {
    try {
      setLoadingPlans(true);
      const type = 'orbitaiaddon';
      const getAddonResponse: any = await getAddon(true, type);
      if (getAddonResponse?.status === 'success') {
        let plans = getAddonResponse?.plans || [];
        if (
          plans.some(
            (p: any) =>
              p?.metadata?.type === type ||
              p?.metadata?.type === 'orbitaiaddon' ||
              p?.metadata?.type === 'orbit_ai_addon'
          )
        ) {
          plans = plans.filter(
            (plan: any) =>
              plan?.metadata?.type === type ||
              plan?.metadata?.type === 'orbitaiaddon' ||
              plan?.metadata?.type === 'orbit_ai_addon'
          );
        }
        setCreditData(plans);

        // Find yearly plan default
        const yearly =
          plans.find((p: any) => p?.recurring?.interval === 'year') ||
          plans.find(
            (p: any) =>
              p?.metadata?.interval === 'yearly' ||
              p?.metadata?.interval === 'year'
          ) ||
          plans[0];

        if (yearly) {
          setPlanId(yearly.id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch Orbit AI add-on plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const yearlyPlan =
    creditData.find((p: any) => p?.recurring?.interval === 'year') ||
    creditData.find(
      (p: any) =>
        p?.metadata?.interval === 'yearly' || p?.metadata?.interval === 'year'
    ) ||
    creditData[0];

  const monthlyPlan =
    creditData.find(
      (p: any) => p?.recurring?.interval === 'month' && p?.id !== yearlyPlan?.id
    ) ||
    creditData.find(
      (p: any) =>
        (p?.metadata?.interval === 'monthly' ||
          p?.metadata?.interval === 'month') &&
        p?.id !== yearlyPlan?.id
    ) ||
    creditData.find((p: any) => p?.id !== yearlyPlan?.id) ||
    creditData[0];

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setDiscountedPrice(null);
    setCouponError('');
  };

  const validateAndApplyCoupon = async (targetPlanIdOverride?: string) => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const effectivePlanId = targetPlanIdOverride || planId;
      const selectedPlan =
        creditData.find((p: any) => p.id === effectivePlanId) || yearlyPlan;
      const targetPlanId = selectedPlan?.id || effectivePlanId;

      const payload = {
        couponId: couponCode?.trim(),
        planId: targetPlanId
      };
      const response: any = await validateCoupon(payload);

      if (response?.status === 'success') {
        const result = response?.data;
        setAppliedCoupon(result?.coupon || couponCode?.trim());
        setDiscountedPrice(result);
        setCouponError('');
      } else {
        setCouponError(response?.message || 'Invalid coupon code');
        setAppliedCoupon(null);
        setDiscountedPrice(null);
      }
    } catch (error: any) {
      setCouponError(
        error?.message || 'Failed to validate coupon. Please try again.'
      );
      setAppliedCoupon(null);
      setDiscountedPrice(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const selectedPlan =
        creditData.find((p: any) => p.id === planId) || yearlyPlan;
      const targetPlanId = selectedPlan?.id || planId;

      handleCloseModal();
      setOpenProcessModal(true);

      const response = await buyOrbitAIAddon({
        email: userEmail,
        planId: targetPlanId,
        couponId: appliedCoupon ? couponCode?.trim() : ""
      });

      if (response?.status === 'success') {
        setSnackBarData(response);
      } else {
        setSnackBarData({
          status: 'error',
          message: response?.message || 'Failed to complete purchase.'
        });
      }
      handleCloseProcessModal();
    } catch (err: any) {
      setSnackBarData({
        status: 'error',
        message: err?.message || 'Failed to complete purchase.'
      });
      handleCloseProcessModal();
    }
  };

  const activePlanId = planId || yearlyPlan?.id;
  const currentPlan = activePlanId === yearlyPlan?.id ? yearlyPlan : monthlyPlan;
  const planOriginalPrice = currentPlan?.unit_amount ? currentPlan.unit_amount / 100 : 0;

  const origPriceDisplay =
    discountedPrice?.originalPrice != null
      ? discountedPrice.originalPrice
      : planOriginalPrice;

  let payablePriceDisplay = planOriginalPrice;
  if (discountedPrice?.discountedPrice != null) {
    payablePriceDisplay = discountedPrice.discountedPrice;
  } else if (discountedPrice?.savings?.percentage != null) {
    payablePriceDisplay = origPriceDisplay * (1 - discountedPrice.savings.percentage / 100);
  } else if (discountedPrice?.discountAmount != null) {
    payablePriceDisplay = origPriceDisplay - discountedPrice.discountAmount;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        backgroundColor: '#fff',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingX: '8px',
          paddingBottom: '32px',
          paddingTop: '12px'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontSize: { xs: '20px', md: '24px' },
            fontWeight: 600,
            marginBottom: '8px',
            color: 'var(--color-text-primary)'
          }}
        >
          Get Orbit And Put 5 AI Agents To Work On Your Site
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px'
          }}
        >
          <Box className="h-8 w-8 flex items-center shrink-0">
            <img
              src="/static/images/icons/ai_visibility.svg"
              alt="copy"
              width={32}
              height={32}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontSize: '14px',
              color: 'var(--color-text-secondary)',
              fontWeight: 400,
              lineHeight: 1.5
            }}
          >
            Orbit sits on top of your SEOSpace plan. Run a single agent if you
            only want reviews handled, or run all five as one standing routine
            across the site.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '24px',
            paddingX: '16px',
            paddingY: '8px',
            marginBottom: '20px',
            alignItems: 'center'
          }}
        >
          {/* Left Content: YouTube Video Player */}
          <Box
            sx={{
              flex: 1,
              width: '100%',
              borderRadius: '8px',
              overflow: 'hidden',
              aspectRatio: '12/9',
              backgroundColor: '#EDEDED'
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/NV_1DmzA0yk"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{
                border: 0,
                display: 'block',
                width: '100%',
                height: '100%'
              }}
            ></iframe>
          </Box>

          {/* Right Content: Plans Selection (Yearly Selected by Default) */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              {[
                { id: 1, title: 'Keyword Assist Agent' },
                { id: 2, title: 'Blog Post Agent' },
                { id: 3, title: 'Content Strategy Agent' },
                { id: 4, title: 'Local SEO Agent' },
                { id: 5, title: 'Review Automation Agent' },
                { id: 6, title: 'Paid Ads, Meta and PR Agents coming soon' }
              ].map((item) => (
                <Box
                  sx={{ display: 'flex', gap: '14px', alignItems: 'center' }}
                  key={item.id}
                >
                  <Box
                    sx={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: '#ECE8FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: '#120242',
                        fontSize: '14px'
                      }}
                    >
                      {item.id}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#5D5F6C',
                      fontSize: '14px'
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ marginX: '16px', marginBottom: '16px' }}>
          {loadingPlans ? (
            <Box
              sx={{
                width: '100%',
                height: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress
                size={30}
                sx={{ color: 'var(--color-primary)' }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: '#F7F4FC',
                border: '1px solid #A8A6BF',
                borderRadius: '8px',
                padding: '10px',
                textAlign: 'center'
              }}
            >
              {/* Header Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  userSelect: 'none',
                  width: 'fit-content',
                  margin: '0 auto'
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#120242',
                    fontFamily: 'Inter'
                  }}
                >
                  Pay Yearly
                </Typography>

                <Box
                  sx={{
                    backgroundColor: '#E0F7EF',
                    border: '1px solid #25D2B0',
                    borderRadius: '8px',
                    paddingX: '8px',
                    paddingY: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#120242',
                      fontFamily: 'Inter'
                    }}
                  >
                    🎉 4 Months Free
                  </Typography>
                </Box>

                <PricingSwitch
                  checked={activePlanId === yearlyPlan?.id}
                  onChange={(e) => {
                    let nextPlanId = yearlyPlan?.id;
                    if (e.target.checked) {
                      if (yearlyPlan?.id) {
                        setPlanId(yearlyPlan.id);
                        nextPlanId = yearlyPlan.id;
                      }
                    } else {
                      if (monthlyPlan?.id) {
                        setPlanId(monthlyPlan.id);
                        nextPlanId = monthlyPlan.id;
                      }
                    }
                    if (appliedCoupon && nextPlanId) {
                      validateAndApplyCoupon(nextPlanId);
                    }
                  }}
                />
              </Box>

              {/* Price Display */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginTop: '4px',
                  rowGap: '2px',
                  columnGap: { xs: '6px', sm: '8px' }
                }}
              >
                {appliedCoupon && discountedPrice ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: { xs: '26px', sm: '36px', md: '40px' },
                        fontWeight: 700,
                        color: '#130145',
                        fontFamily: 'Inter',
                        lineHeight: 1.2
                      }}
                    >
                      {`$${Number(payablePriceDisplay).toFixed(2)}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '16px', sm: '20px', md: '24px' },
                        fontWeight: 600,
                        color: '#8E8EA9',
                        textDecoration: 'line-through',
                        fontFamily: 'Inter',
                        lineHeight: 1.2
                      }}
                    >
                      {`$${Number(origPriceDisplay).toFixed(2)}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '16px', sm: '20px' },
                        fontWeight: 700,
                        color: '#130145',
                        fontFamily: 'Inter',
                        lineHeight: 1.2
                      }}
                    >
                      {activePlanId === yearlyPlan?.id ? '/year' : '/mo'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: { xs: '28px', sm: '36px', md: '40px' },
                        fontWeight: 700,
                        color: '#130145',
                        fontFamily: 'Inter',
                        lineHeight: 1.2
                      }}
                    >
                      {`$${
                        activePlanId === yearlyPlan?.id
                          ? yearlyPlan?.unit_amount
                            ? (yearlyPlan.unit_amount / 100).toFixed(2)
                            : ''
                          : monthlyPlan?.unit_amount
                          ? (monthlyPlan.unit_amount / 100).toFixed(2)
                          : ''
                      }`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '16px', sm: '20px' },
                        fontWeight: 700,
                        color: '#130145',
                        fontFamily: 'Inter',
                        marginLeft: '4px',
                        lineHeight: 1.2
                      }}
                    >
                      {activePlanId === yearlyPlan?.id ? '/year' : '/mo'}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ marginX: '16px' }}>
          <CouponSection
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            couponError={couponError}
            couponLoading={couponLoading}
            appliedCoupon={appliedCoupon}
            discountedPrice={discountedPrice}
            validateAndApplyCoupon={validateAndApplyCoupon}
            removeCoupon={removeCoupon}
            isAnnual={activePlanId === yearlyPlan?.id}
            isButtonDisabled={isPending || loadingPlans}
          />
        </Box>

        {alertData?.status && (
          <Box sx={{ marginX: '16px', marginBottom: '16px' }}>
            <CommonAlert
              severity={alertData?.status}
              message={alertData?.message}
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            marginX: '8px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column'
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              disabled={isPending}
              sx={{
                flex: 1,
                display: 'flex',
                padding: '12px',
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)'
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: 'inherit'
                }}
              >
                Cancel
              </Typography>
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column'
            }}
          >
            <Button
              variant="contained"
              onClick={
                isPending || isMember || loadingPlans
                  ? undefined
                  : handleUpgrade
              }
              disabled={isPending || isMember || loadingPlans}
              sx={{
                flex: 1,
                display: 'flex',
                padding: '12px',
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                borderRadius: '8px',
                boxShadow: '0px 4px 10px rgba(84, 52, 211, 0.2)',
                '&:hover': {
                  backgroundColor: 'var(--color-primary)',
                  opacity: 0.9,
                  boxShadow: '0px 6px 14px rgba(84, 52, 211, 0.3)'
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isPending ? (
                  <CircularProgress size={18} sx={{ color: '#fff' }} />
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '16px',
                        color: 'var(--color-white)'
                      }}
                    >
                      Upgrade To Orbit AI
                    </Typography>
                    <img
                      src="/static/images/icons/ai_visibility_white.svg"
                      alt="copy"
                      width={20}
                      height={20}
                    />
                  </>
                )}
              </Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const OrbitAIAddonModal = ({
  open,
  onClose
}: OrbitAIAddonModalProps) => {
  const isEnterprisePlan = useSelector(
    (state: RootState) =>
      state.user?.isEnterprisePlan ||
      state.user?.activePlan === state.user?.planDetails?.enterprise
  );
  const isAlreadyPurchased = useSelector(
    (state: RootState) =>
      Boolean((state.counter?.userDetails as any)?.isOrbitAiAddonPurchased) ||
      Boolean((state.counter?.userDetails as any)?.is_orbit_ai_addon_purchased) ||
      Boolean(state.dashboard?.isAiAddonPurchased)
  );

  const [openProcessModal, setOpenProcessModal] = useState(false);
  const [snackBarData, setSnackBarData] = useState<any>(null);
  const [openSnackBar, setOpenSnackBar] = useState(false);

  if (isEnterprisePlan || isAlreadyPurchased) return null;

  const handleCloseProcessModal = () => {
    setOpenProcessModal(false);
    setOpenSnackBar(true);
    setTimeout(() => {
      setOpenSnackBar(false);
      setSnackBarData(null);
      onClose();
    }, 4000);
  };

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
    setSnackBarData(null);
  };

  return (
    <>
      {snackBarData?.status && openSnackBar && (
        <Snackbar
          sx={{ marginTop: '2.8rem' }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(snackBarData?.status && openSnackBar)}
          autoHideDuration={4000}
          onClose={handleCloseSnackBar}
        >
          <Box>
            <Alert
              onClose={handleCloseSnackBar}
              severity={snackBarData?.status}
              variant="filled"
              sx={{ maxWidth: '667px' }}
            >
              {snackBarData?.message}
            </Alert>
          </Box>
        </Snackbar>
      )}

      <CommonModal
        open={open}
        handCloseModal={onClose}
        title=""
        closebtn={false}
        blur={false}
        isDynamicWidth="727px"
        disableScrollLock={true}
      >
        <OrbitAIAddonContent
          handleCloseModal={onClose}
          handleCloseProcessModal={handleCloseProcessModal}
          setOpenProcessModal={setOpenProcessModal}
          setSnackBarData={setSnackBarData}
        />
      </CommonModal>

      <CommonModal
        open={openProcessModal}
        handCloseModal={() => setOpenProcessModal(false)}
        title=""
        closebtn={false}
        blur={false}
        isBuySearchCredit={false}
      >
        <GenerateIdeasLoader title="Please wait while we are processing your Orbit AI request." />
      </CommonModal>
    </>
  );
};

export default OrbitAIAddonModal;
